import React from 'react';
import { useTranslation } from './i18n.js';
import { apiUrl } from './config.js';
import { CollapsibleCard } from './collapsible.js';

const DISTRIBUTOR_TYPES = [
  { key: 'PROSVETA', labelKey: 'typeProsveta' },
  { key: 'COEDITOR', labelKey: 'typeCoeditor' },
];

const DISTRIBUTOR_CONTINENTS = [
  { key: 'EUROPE', labelKey: 'continentEurope', match: ['EUROPA'] },
  { key: 'AMERICA', labelKey: 'continentAmerica', match: ['AMERICA'] },
  { key: 'AFRICA', labelKey: 'continentAfrica', match: ['AFRICA'] },
  { key: 'MIDDLE_EAST', labelKey: 'continentMiddleEast', match: ['MIDDLE EAST'] },
  { key: 'ASIA', labelKey: 'continentAsia', match: ['ASIA'] },
  { key: 'OCEANIA', labelKey: 'continentOceania', match: ['OCEANIA'] },
];

function getDistributorSiteUrl(site) {
  if (!site) {
    return '';
  }
  return /^https?:\/\//i.test(site) ? site : `https://${site}`;
}

// `label` (API) est une ou plusieurs étiquettes séparées par `|` (ex. "Editeur modial|Distributeur")
// décrivant le rôle du distributeur. "modial" est une coquille de l'API pour "mondial" : on la
// reconnaît telle quelle (en plus de l'orthographe correcte) sans la corriger dans l'affichage des
// autres champs, qui restent tels que fournis.
const DISTRIBUTOR_LABEL_KEYS = {
  Editeur: 'distributorLabelPublisher',
  'Editeur modial': 'distributorLabelWorldPublisher',
  'Editeur mondial': 'distributorLabelWorldPublisher',
  Distributeur: 'distributorLabelDistributor',
  Diffuseur: 'distributorLabelDiffuser',
};

function DistributorLabels({ label, t }) {
  const terms = String(label || '')
    .split('|')
    .map((term) => term.trim())
    .filter(Boolean);

  if (terms.length === 0) {
    return null;
  }

  const text = terms.map((term) => (DISTRIBUTOR_LABEL_KEYS[term] ? t(DISTRIBUTOR_LABEL_KEYS[term]) : term)).join(' - ');

  return <p className="distributor-card__labels">{text}</p>;
}

function DistributorCard({ item, className = 'distributor-card' }) {
  const { t } = useTranslation();
  const siteUrl = getDistributorSiteUrl(item.site);
  const flagUrl = item.square_flag || item.flag;

  return (
    <div className={className}>
      {flagUrl && (
        <img className="distributor-card__flag" src={flagUrl} alt={item.pays} loading="lazy" />
      )}
      <h3 className="distributor-card__country">{item.pays}</h3>
      {item.corporate_name && (
        <p className="distributor-card__name">{item.corporate_name}</p>
      )}
      <DistributorLabels label={item.label} t={t} />
      {item.email && (
        <p className="distributor-card__email">
          <a href={`mailto:${item.email}`}>{item.email}</a>
        </p>
      )}
      {siteUrl && (
        <p className="distributor-card__site">
          <a href={siteUrl} target="_blank" rel="noopener noreferrer">
            {item.site}
          </a>
        </p>
      )}
    </div>
  );
}

export function DistributorsCard({ initialData }) {
  const { t } = useTranslation();
  const [distributors, setDistributors] = React.useState(initialData ? initialData.distributors : null);
  const [defaultDistributor, setDefaultDistributor] = React.useState(initialData ? initialData.defaultDistributor : null);
  const [loading, setLoading] = React.useState(!initialData);
  const [error, setError] = React.useState('');
  const [selectedType, setSelectedType] = React.useState(null);
  const [selectedContinent, setSelectedContinent] = React.useState(null);

  React.useEffect(() => {
    if (initialData) {
      return;
    }

    const distributorsUrl = apiUrl('distributors', { type: 'ALL' });
    if (window.distributor) {
      distributorsUrl.searchParams.set('distributor', window.distributor);
    }
    if (window.country) {
      distributorsUrl.searchParams.set('country', window.country);
    }
    fetch(distributorsUrl.toString(), { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) {
          throw new Error('load-error');
        }
        return response.json();
      })
      .then((data) => {
        setDistributors((data && data.distributors) || {});
        const defaultDist = (data && data.default_distributor) || null;
        setDefaultDistributor(defaultDist);

        if (!window.distributor && defaultDist && defaultDist.distributor) {
          window.distributor = String(defaultDist.distributor);
        }
      })
      .catch(() => {
        setError(t('distributorsErrorLoad'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <section className="distributors-card" id="distributors">
        <div className="loading">
          <progress />
          <p>{t('distributorsLoading')}</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="distributors-card" id="distributors">
        <div className="error">
          <strong>{t('distributorsErrorPrefix')}</strong> {error}
        </div>
      </section>
    );
  }

  const hasSelection = Boolean(selectedType && selectedContinent);
  const continentGroup = selectedContinent
    ? DISTRIBUTOR_CONTINENTS.find((continent) => continent.key === selectedContinent)
    : null;
  const typeEntries = (selectedType && distributors && distributors[selectedType]) || {};

  const items = continentGroup
    ? Object.values(typeEntries)
        .filter((item) => {
          const continentCode = String(item.continent || '').trim().toUpperCase();
          return continentGroup.match.includes(continentCode);
        })
        .sort((a, b) => String(a.pays).localeCompare(String(b.pays)))
    : [];

  return (
    <CollapsibleCard id="distributors" className="distributors-card" label={t('distributorsTitle')}>
      {defaultDistributor && (
        <div className="distributors-default">
          <h2>{t('distributorsDefaultTitle')}</h2>
          <DistributorCard item={defaultDistributor} className="distributors-default-info" />
        </div>
      )}

      <h2>{t('distributorsTitle')}</h2>

      <div className="distributors-filters">
        <div className="distributors-filter-group" role="group" aria-label={t('distributorsTitle')}>
          {DISTRIBUTOR_TYPES.map((type) => (
            <button
              key={type.key}
              type="button"
              className={`distributors-filter-btn ${selectedType === type.key ? 'is-active' : ''}`}
              onClick={() => setSelectedType(type.key)}
            >
              {t(type.labelKey)}
            </button>
          ))}
        </div>

        <div className="distributors-filter-group distributors-filter-group--continents" role="group">
          {DISTRIBUTOR_CONTINENTS.map((continent) => (
            <button
              key={continent.key}
              type="button"
              className={`distributors-filter-btn distributors-filter-btn--continent ${selectedContinent === continent.key ? 'is-active' : ''}`}
              onClick={() => setSelectedContinent(continent.key)}
            >
              {t(continent.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {!hasSelection ? (
        <p className="distributors-select-prompt">{t('distributorsSelectPrompt')}</p>
      ) : items.length === 0 ? (
        <p className="distributors-empty">{t('distributorsEmpty')}</p>
      ) : (
        <div className="distributors-grid">
          {items.map((item) => (
            <DistributorCard item={item} key={item.distributor_id} />
          ))}
        </div>
      )}
    </CollapsibleCard>
  );
}
